import * as React from 'react';
import {
    makeStyles,
    shorthands,
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    DialogSurface,
    DialogActions,
    DialogBody,
    Dropdown,
    Option
} from '@fluentui/react-components';
import { apiClient } from '../api';
import Loader from './Loader';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastProvider';
import { addToStoredMapping, getStoredCitationMapping, buildArticleIdToNumberMap } from '../utils/documentProperties';

/**
 * Superscript Unicode character map for AMA style
 */
const SUPERSCRIPT_MAP = {
    '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
    '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹'
};

/**
 * Convert a number to superscript Unicode string
 * @param {number|string} num - Number to convert
 * @returns {string} - Superscript Unicode string
 */
function toSuperscript(num) {
    return num.toString().split('').map(digit => SUPERSCRIPT_MAP[digit] || digit).join('');
}

const useStyles = makeStyles({
    container: {
        height: '100%',
        height: '100%',
        backgroundColor: '#eee',
        padding: '20px',
        borderRadius: '0px',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
    },
    buttons: {
        display: 'flex',
        flexDirection: 'row!important',
        justifyContent: 'flex-start',
        marginTop: '20px',
        ...shorthands.gap('10px'),

        '& .fui-Dropdown': {
            minWidth: '165px',
        }
    },
    btnClose: {
        position: 'absolute',
        top: '10px',
        right: '5px',
        padding: '0px',
        justifyContent: 'flex-end',
        float: 'right',
    },
});

export const ArticleDetailDialog = ({ open, article, onClose }) => {
    const styles = useStyles();
    const [loading, setLoading] = React.useState(false);
    const [selectedStyle, setSelectedStyle] = React.useState('');
    const [citation, setCitation] = React.useState('');
    const { showToast } = useToast();
    const { citationStyles, selectedCitationStyle } = useAuth();

    React.useEffect(() => {
        setSelectedStyle(selectedCitationStyle);
    }, [selectedCitationStyle]);

    React.useEffect(() => {
        if (selectedStyle && open) {
            fetchCitation();
        }
    }, [selectedStyle, open]);

    const fetchCitation = async () => {
        setLoading(true);
        try {
            // Fetch both in-text citation and bibliography
            const response = await apiClient.getCitations(
                selectedStyle, 
                [article?.id],
                "both",  // Get both in-text citation and bibliography
                "text"   // Use text format
            );
            // Extract the in-text citation from the response
            if (response.citations && response.citations.length > 0) {
                setCitation(response.citations[0].in_text);
            }
        } catch (error) {
            showToast("Error fetching citation styles", error?.message, { intent: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleInsertCitation = async () => {
        try {
            // Load stored mapping to dedupe by article_id and reuse numbers
            const storedMapping = await getStoredCitationMapping();
            const articleIdToNumber = buildArticleIdToNumberMap(storedMapping);
            let maxExistingNumber = 0;
            for (const n of articleIdToNumber.values()) {
                if (n > maxExistingNumber) maxExistingNumber = n;
            }

            // Fetch full citation data with bibliography
            const response = await apiClient.getCitations(
                selectedStyle,
                [article?.id],
                "both",
                "text"
            );

            Word.run(async (context) => {
                const selection = context.document.getSelection();
                const body = context.document.body;

                let citations = response.citations || [];
                let bibliography = response.bibliography || [];

                if (citations.length > 0) {
                    const searchResults = body.search("References", { matchCase: false, matchWholeWord: true });
                    context.load(searchResults, "items");
                    await context.sync();
                    const hasReferencesSection = searchResults.items.length > 0;

                    const style = selectedStyle.toLowerCase();
                    const isNumberedStyle = ['vancouver', 'ieee', 'ama', 'critical-care'].includes(style);
                    const articleId = article?.id;

                    let citationNum;
                    let isNewArticle;
                    if (articleIdToNumber.has(articleId)) {
                        citationNum = articleIdToNumber.get(articleId);
                        isNewArticle = false;
                    } else {
                        citationNum = maxExistingNumber + 1;
                        articleIdToNumber.set(articleId, citationNum);
                        isNewArticle = true;
                    }

                    if (isNumberedStyle) {
                        if (style === 'vancouver' || style === 'critical-care') {
                            citations[0].in_text = `(${citationNum})`;
                        } else if (style === 'ieee') {
                            citations[0].in_text = `[${citationNum}]`;
                        } else if (style === 'ama') {
                            citations[0].in_text = toSuperscript(citationNum);
                        }
                    }
                    if (bibliography[0]) {
                        const original = bibliography[0];
                        if (/^\[\d+\]/.test(original)) {
                            bibliography[0] = original.replace(/^\[\d+\]/, `[${citationNum}]`);
                        } else if (/^\d+\./.test(original)) {
                            bibliography[0] = original.replace(/^\d+\./, `${citationNum}.`);
                        } else {
                            // Author-date styles (APA, Chicago, ...) return
                            // entries with no leading number — prepend one.
                            bibliography[0] = `${citationNum}. ${original}`;
                        }
                    }

                    // Insert in-text citation at cursor
                    let citationText = citations[0].in_text;
                    if (style === 'ama') citationText = citationText + ' ';
                    selection.insertText(citationText, Word.InsertLocation.replace);
                    selection.getRange(Word.RangeLocation.end).select();

                    // Add bibliography entry only if this is a new article
                    if (isNewArticle && bibliography.length > 0) {
                        if (!hasReferencesSection) {
                            const endParagraph = body.paragraphs.getLast();
                            endParagraph.insertParagraph("References", Word.InsertLocation.after);
                            endParagraph.insertParagraph("", Word.InsertLocation.after);
                            endParagraph.insertParagraph("", Word.InsertLocation.after);
                        }

                        let insertPoint = body.paragraphs.getLast();
                        context.load(insertPoint, "text");
                        await context.sync();

                        // Walk back past trailing empty paragraphs so the new entry
                        // attaches directly after the last bibliography entry.
                        while (insertPoint.text.trim() === "") {
                            const prev = insertPoint.getPreviousOrNullObject();
                            context.load(prev, "text,isNullObject");
                            await context.sync();
                            if (prev.isNullObject) break;
                            insertPoint = prev;
                        }

                        insertPoint.insertParagraph(bibliography[0], Word.InsertLocation.after);
                    }
                }

                await context.sync();
                
                // Save citation to document properties for style conversion
                if (citations.length > 0) {
                    try {
                        let insertedText = citations[0].in_text;
                        // For AMA style, add space (matching what was inserted)
                        if (selectedStyle.toLowerCase() === 'ama') {
                            insertedText = insertedText + ' ';
                        }
                        
                        const citationToSave = {
                            article_id: article?.id,
                            inserted_text: insertedText,
                            group_id: `g_${Date.now()}_${Math.random()}`,
                            citation_number: citations[0].citation_number
                        };
                        
                        // Get base citation in Chicago format for better conversion support
                        let baseCitation = null;
                        try {
                            const baseResponse = await apiClient.getCitations(
                                'chicago',
                                [article?.id],
                                'both',
                                'text'
                            );
                            
                            if (baseResponse && baseResponse.citations && baseResponse.citations.length > 0) {
                                baseCitation = [{
                                    article_id: article?.id,
                                    inserted_text: baseResponse.citations[0].in_text,
                                    group_id: citationToSave.group_id,
                                    citation_number: baseResponse.citations[0].citation_number
                                }];
                            }
                        } catch (baseError) {
                            // Continue without base citation
                        }

                        // Save citation to document properties
                        await addToStoredMapping([citationToSave], selectedStyle, baseCitation);
                    } catch (saveError) {
                        // Don't fail the entire operation if saving fails
                    }
                }
                onClose();

            }).catch((error) => {
                showToast("Error inserting citation", error?.message, { intent: "error" });
            });
        } catch (error) {
            showToast("Error inserting citation", error?.message, { intent: "error" });
        }
    }

    return (
        <Dialog open={open} inertTrapFocus>
            <DialogSurface className={styles.container}>
                <Loader loading={loading} />
                <DialogBody style={{ height: '100%' }}>
                    <DialogTitle style={{ paddingRight: '15px' }}>
                        {article?.title}
                        <Button
                            appearance="transparent"
                            className={styles.btnClose}
                            onClick={onClose}
                        >
                            <img src="/assets/close.png" alt="Close icon" />
                        </Button>
                    </DialogTitle>
                    <DialogContent className={styles.content}>
                        <div
                            dangerouslySetInnerHTML={{ __html: article?.abstract }}
                            style={{ lineHeight: "1.5" }}
                        />
                        <br />
                        <strong>{citation}</strong>
                    </DialogContent>
                    <DialogActions className={styles.buttons}>
                        <div style={{ flex: 1, minWidth: 0, maxWidth: "100%" }}>
                            <Dropdown
                                placeholder="Select Citation Style"
                                appearance="filled-lighter"
                                positioning={{ position: 'above', autoSize: true, pinned: true }}
                                value={selectedStyle?.toUpperCase()}
                                selectedOptions={selectedStyle ? [selectedStyle] : []}
                                onOptionSelect={(_, data) => setSelectedStyle(data.optionValue)}
                            >
                                {citationStyles.map((style) => (
                                    <Option key={style} value={style}>
                                        {style.toUpperCase()}
                                    </Option>
                                ))}
                            </Dropdown>
                        </div>


                        <Button appearance="primary" onClick={handleInsertCitation} disabled={!citation}>
                            Insert
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
